/**
 * What the client page's popups send: New client and Edit client, Add and
 * Edit location, Add contact. Each is checked here, the same way on the
 * screen and the server.
 */
import { z } from 'zod'
import { languages } from '../contacts/contact.ts'
import { patchOf } from '../shared/patch.ts'
import { recordIdSchema } from '../shared/record-id.ts'
import { billingMethods, paymentTerms, serviceStatuses, taxCodes } from './client.ts'

export const clientFormSchema = z.object({
  name: z.string().trim().min(1, 'Give the client a name.').max(160),
  paymentTerms: z.enum(paymentTerms),
  taxCode: z.enum(taxCodes),
  billingMethod: z.enum(billingMethods),
  notes: z.string().max(4000).default(''),
})
export type ClientForm = z.infer<typeof clientFormSchema>

export const locationFormSchema = z.object({
  name: z.string().trim().min(1, 'Give the location a name.').max(160),
  addressLine1: z.string().trim().min(1, 'Add the street address.').max(200),
  addressLine2: z.string().trim().max(120).default(''),
  city: z.string().trim().min(1, 'Add the city.').max(80),
  state: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{2}$/, 'Two letters, like NY.')
    .transform((s) => s.toUpperCase()),
  postalCode: z
    .string()
    .trim()
    .regex(/^\d{5}$/, 'A ZIP code is five digits.'),
  workNotes: z.string().max(4000).default(''),
  accessNotes: z.string().max(4000).default(''),
})
export type LocationForm = z.infer<typeof locationFormSchema>

export const locationPatchSchema = patchOf(locationFormSchema).extend({
  status: z.enum(serviceStatuses).optional(),
  /** The location's "we miss you" reminder, on or off. */
  remindersEnabled: z.boolean().optional(),
})

/** Add contact: either someone already in the contacts, or a new person. */
export const personFormSchema = z
  .object({
    clientId: recordIdSchema,
    /** One location only, or the whole client. */
    locationId: recordIdSchema.nullable().default(null),
    /** "Main contact", "Contact", "Billing contact — gets invoices", "Other". */
    kind: z.enum(['main', 'contact', 'billing', 'other']),
    contactId: recordIdSchema.nullable().default(null),
    firstName: z.string().trim().max(80).default(''),
    lastName: z.string().trim().max(80).default(''),
    phone: z.string().trim().max(30).default(''),
    email: z.string().trim().max(200).default(''),
    language: z.enum(languages).default('english'),
  })
  .superRefine((value, ctx) => {
    if (value.contactId) return
    if (!value.firstName)
      ctx.addIssue({ code: 'custom', path: ['firstName'], message: 'Add their name.' })
    if (!value.phone && !value.email) {
      ctx.addIssue({ code: 'custom', path: ['phone'], message: 'Add a phone or an email.' })
    }
    if (value.email && !z.email().safeParse(value.email).success) {
      ctx.addIssue({ code: 'custom', path: ['email'], message: 'That email doesn’t look right.' })
    }
    if (value.phone && e164(value.phone) === null) {
      ctx.addIssue({ code: 'custom', path: ['phone'], message: 'A phone number has ten digits.' })
    }
  })
export type PersonForm = z.infer<typeof personFormSchema>

/** '(212) 555-0100', '212.555.0100' or '2125550100' → '+12125550100'. */
export function e164(phone: string): string | null {
  const digits = phone.replace(/\D/g, '')
  if (digits.length === 10) return `+1${digits}`
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
  return null
}
